import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Badge } from "react-bootstrap";
import { FaTimes, FaRupeeSign, FaTrashAlt, FaShoppingCart, FaArrowRight } from "react-icons/fa";
import "./FloatingCart.css";

// Helper function to round to nearest whole number
const roundToNearestRupee = (amount) => {
  return Math.round(amount);
};

const FloatingCart = ({ isOpen, onClose, cartItems, discountvalue, onUpdateCart, onCountsUpdate }) => {
  const [items, setItems] = useState(cartItems || []);
  const navigate = useNavigate();

  useEffect(() => {
    setItems(cartItems || []);
  }, [cartItems]);

  // Handle quantity change in floating cart
  const handleQuantityChange = (productId, change) => {
    const updatedItems = items.map((item) => {
      if (item.product_id === productId) {
        const newCount = item.count + change;
        if (newCount < 1) return { ...item, count: 1 };
        return { ...item, count: newCount };
      }
      return item;
    });

    setItems(updatedItems);
    localStorage.setItem("cartItems", JSON.stringify(updatedItems));

    // Update counts in localStorage
    const updatedCounts = updatedItems.reduce((acc, item) => {
      acc[item.product_id] = item.count;
      return acc;
    }, {});
    
    // Save counts to localStorage
    Object.entries(updatedCounts).forEach(([productId, count]) => {
      localStorage.setItem(`count_${productId}`, count);
    });

    // Remove counts for items no longer in cart
    const currentProductIds = new Set(updatedItems.map(item => item.product_id));
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('count_')) {
        const productId = key.replace('count_', '');
        if (!currentProductIds.has(productId)) {
          localStorage.removeItem(key);
        }
      }
    });

    // IMPORTANT: cartCount should be the number of UNIQUE products (items.length)
    const uniqueProductCount = updatedItems.length;
    localStorage.setItem("cartCount", uniqueProductCount);
    
    if (onUpdateCart) onUpdateCart(updatedItems);
    if (onCountsUpdate) onCountsUpdate(updatedCounts);
    
    window.dispatchEvent(new Event("storage"));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  // Handle delete item from cart
  const handleDelete = (productId) => {
    const updatedItems = items.filter(item => item.product_id !== productId);
    setItems(updatedItems);
    localStorage.setItem("cartItems", JSON.stringify(updatedItems));
    localStorage.removeItem(`count_${productId}`);
    
    // IMPORTANT: cartCount should be the number of UNIQUE products (items.length)
    const uniqueProductCount = updatedItems.length;
    localStorage.setItem("cartCount", uniqueProductCount);

    if (onUpdateCart) onUpdateCart(updatedItems);
    
    const updatedCounts = updatedItems.reduce((acc, item) => {
      acc[item.product_id] = item.count;
      return acc;
    }, {});
    if (onCountsUpdate) onCountsUpdate(updatedCounts);
    
    window.dispatchEvent(new Event("storage"));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  // Calculate totals
  const getSubtotal = () => {
    return roundToNearestRupee(
      items.reduce((total, item) => {
        const price = parseFloat(item.oldprice.replace(/[^\d.-]/g, ""));
        if (!isNaN(price)) {
          return total + (item.count * price);
        }
        return total;
      }, 0)
    );
  };

  const getDiscountedTotal = () => {
    const discountAmount = items.reduce((total, item) => {
      if (item.is_non_discount === "0") {
        const discount = Math.round(discountvalue);
        const price = parseFloat(item.oldprice.replace(/[^\d.-]/g, ""));
        if (!isNaN(price)) {
          const itemTotal = item.count * price;
          const discountedItemTotal = (itemTotal * discount) / 100;
          return total + discountedItemTotal;
        }
      }
      return total;
    }, 0);
    return roundToNearestRupee(discountAmount);
  };

  const getOrderTotal = () => {
    const subtotal = getSubtotal();
    const discountTotal = getDiscountedTotal();
    return roundToNearestRupee(subtotal - discountTotal);
  };

  // Get total quantity of all items (sum of all counts) - for display only
  const getTotalQuantity = () => {
    return items.reduce((acc, item) => acc + item.count, 0);
  };

  // Get number of unique products - THIS IS WHAT WE SHOW IN THE BADGE
  const getUniqueProductCount = () => {
    return items.length;
  };

  const subtotal = getSubtotal();
  const discountTotal = getDiscountedTotal();
  const orderTotal = getOrderTotal();
  const totalQuantity = getTotalQuantity();
  const uniqueProductCount = getUniqueProductCount(); // This is the product count
  const discountRate = Math.round(discountvalue || 0);

  // Handle checkout
  const handleCheckout = () => {
    onClose();
    navigate("/cart");
  };

  // Handle continue shopping
  const handleContinueShopping = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fc-overlay" onClick={onClose}></div>
      
      {/* Cart Sidebar */}
      <div className={`fc-sidebar ${isOpen ? 'fc-open' : ''}`}>
        {/* Header */}
        <div className="fc-header">
          <div className="fc-header-left">
            <FaShoppingCart className="fc-header-icon" />
            <span className="fc-header-title">Your Cart</span>
            {/* This shows UNIQUE PRODUCT COUNT - NOT total quantity */}
            <Badge className="fc-header-badge">{uniqueProductCount}</Badge>
          </div>
          <button className="fc-close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className="fc-body">
          {items.length === 0 ? (
            <div className="fc-empty-state">
              <FaShoppingCart className="fc-empty-icon" />
              <h5>Your cart is empty</h5>
              <p>Add some crackers to get started!</p>
              <Button variant="primary" onClick={handleContinueShopping}>
                Continue Shopping
              </Button>
            </div>
          ) : (
            <>
              {/* Table Header - NEW */}
              <div className="fc-table-header">
                <div className="fc-header-col fc-header-image">Image</div>
                <div className="fc-header-col fc-header-particulars">Particulars</div>
                <div className="fc-header-col fc-header-total">Total</div>
              </div>

              {/* Cart Items List - Scrollable */}
              <div className="fc-items-list">
                {items.map((item) => {
                  const isNonDiscount = parseInt(item.is_non_discount, 10) === 1;
                  const roundedOldPrice = item.oldprice ? roundToNearestRupee(parseFloat(item.oldprice.replace(/[^\d.-]/g, ""))) : 0;
                  const roundedPrice = item.price ? roundToNearestRupee(parseFloat(item.price.replace(/[^\d.-]/g, ""))) : 0;
                  const itemTotal = roundToNearestRupee(
                    (isNonDiscount ? roundedOldPrice : roundedPrice) * item.count
                  );

                  return (
                    <div key={item.product_id} className="fc-item">
                      <div className="fc-item-image">
                        <img src={item.image} alt={item.product_name} />
                      </div>
                      <div className="fc-item-details">
                        <div className="fc-item-name">{item.product_name}</div>
                        <div className="fc-item-price">
                          {isNonDiscount ? (
                            <span className="fc-current-price">
                              <FaRupeeSign /> {roundedOldPrice}
                            </span>
                          ) : (
                            <>
                              <del className="fc-old-price">
                                <FaRupeeSign /> {roundedOldPrice}
                              </del>
                              <span className="fc-current-price">
                                <FaRupeeSign /> {roundedPrice}
                              </span>
                            </>
                          )}
                        </div>
                        <div className="fc-item-actions">
                          <div className="fc-qty-control">
                            <button
                              className="fc-qty-btn"
                              onClick={() => handleQuantityChange(item.product_id, -1)}
                            >
                              −
                            </button>
                            <span className="fc-qty-number">{item.count}</span>
                            <button
                              className="fc-qty-btn"
                              onClick={() => handleQuantityChange(item.product_id, 1)}
                            >
                              +
                            </button>
                          </div>
                          <button
                            className="fc-delete-btn"
                            onClick={() => handleDelete(item.product_id)}
                          >
                            <FaTrashAlt />
                          </button>
                        </div>
                      </div>
                      <div className="fc-item-total">
                        <FaRupeeSign /> {itemTotal}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Summary - Fixed at bottom */}
              <div className="fc-summary">
                <div className="fc-summary-row">
                  <span>Subtotal</span>
                  <span><FaRupeeSign /> {subtotal}</span>
                </div>
                {discountRate > 0 && discountTotal > 0 && (
                  <div className="fc-summary-row fc-discount-row">
                    <span>Discount ({discountRate}%)</span>
                    <span className="fc-discount-amount">- <FaRupeeSign /> {discountTotal}</span>
                  </div>
                )}
                <div className="fc-summary-row fc-total-row">
                  <span>Total</span>
                  <span><FaRupeeSign /> {orderTotal}</span>
                </div>
                <div className="fc-item-count">
                  <span>
                    {/* Show UNIQUE PRODUCT COUNT - just like Header */}
                    {uniqueProductCount} product{uniqueProductCount !== 1 ? 's' : ''}
                    {/* Optionally show total quantity in brackets */}
                    {totalQuantity > uniqueProductCount && (
                      <span className="fc-total-qty"> (Total Qty: {totalQuantity})</span>
                    )}
                  </span>
                </div>
                <div className="fc-actions">
                  <Button variant="outline-primary" className="fc-continue-btn" onClick={handleContinueShopping}>
                    Continue Shopping
                  </Button>
                  <Button variant="primary" className="fc-checkout-btn" onClick={handleCheckout}>
                    Checkout <FaArrowRight />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default FloatingCart;